import React, { useState, useEffect, useRef } from 'react';
import '../styles/purchase-modal.css';
import { selectNodeForPurchase, getActiveNodes } from '../services/nodeService';
import { createPaymentOrder } from '../services/paymentService';
import NodeSelector from './NodeSelector';
import PaymentComponent from './PaymentComponent';

/**
 * Enhanced PurchaseModal Component
 * Handles product purchase with better wallet integration and responsive design
 */
const PurchaseModal = ({ isOpen, onClose, product, wallet, onPurchase }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [confirmationStep, setConfirmationStep] = useState(1);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [transaction, setTransaction] = useState(null);
  // Add state for agreement checkboxes
  const [agreements, setAgreements] = useState({
    riskDisclosure: false,
    purchaseAgreement: false
  });
  
  // Add state for node selection
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [isLoadingNodes, setIsLoadingNodes] = useState(false);
  const [nodeError, setNodeError] = useState(null);
  const [nodes, setNodes] = useState([]);
  
  // Add state for payment
  const [paymentOrder, setPaymentOrder] = useState(null);
  const [showPaymentComponent, setShowPaymentComponent] = useState(false);
  
  const modalRef = useRef(null);
  
  // Utility function to get network ID from wallet in the format needed
  const getWalletNetworkId = (wallet) => {
    // Use network name if available
    if (wallet.network) {
      return wallet.network; // This will be 'ethereum', 'bsc', 'solana', etc.
    }
    
    // Fallback to chainId for EVM chains
    if (wallet.chainId) {
      // Convert chainId hex to network name
      if (wallet.chainId === '0x1') return 'ethereum';
      if (wallet.chainId === '0x38') return 'bsc';
      if (wallet.chainId === '0x89') return 'polygon';
      
      // If we have an unknown chainId, just return it
      return wallet.chainId;
    }
    
    // Ultimate fallback
    return 'ethereum';
  };
  
  // Check if user is on mobile
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      setIsMobile(/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase()));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setIsProcessing(false);
      setConfirmationStep(1);
      setError(null);
      setTransaction(null);
      setAgreements({
        riskDisclosure: false,
        purchaseAgreement: false
      });
      setSelectedNodeId(null);
      setShowPaymentComponent(false);
      setPaymentOrder(null);
    }
  }, [isOpen]);
  
  // When modal opens and wallet is connected, fetch available nodes
  useEffect(() => {
    if (!isOpen || !wallet.connected || confirmationStep !== 1) return;
    
    const fetchNodes = async () => {
      setIsLoadingNodes(true);
      setNodeError(null);
      
      try {
        // Get network ID in a consistent format
        const networkId = getWalletNetworkId(wallet);
        
        console.log("Fetching nodes with:", {
          walletAddress: wallet.address,
          networkId: networkId,
          walletType: wallet.type
        });
        
        const result = await getActiveNodes(
          wallet.address,
          networkId,
          wallet.type
        );
        
        if (result.success) {
          setNodes(result.nodes || []);
          
          // Optionally auto-select user's own node if available
          if (result.nodes && result.nodes.length > 0) {
            const userNodes = result.nodes.filter(node => 
              node.wallet_address.toLowerCase() === wallet.address.toLowerCase()
            );
            
            if (userNodes.length > 0) {
              setSelectedNodeId(userNodes[0].id);
            }
          }
        } else {
          setNodeError(result.message || 'Failed to load nodes');
        }
      } catch (error) {
        console.error('Error fetching nodes:', error);
        setNodeError('Error loading nodes. Please try again.');
      } finally {
        setIsLoadingNodes(false);
      }
    };
    
    fetchNodes();
  }, [isOpen, wallet, confirmationStep, product]);
  
  // Close modal if escape key is pressed
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !isProcessing) {
        onClose();
      }
    };
    
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isProcessing, onClose]);

  // Handle clicks outside modal to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target) && !isProcessing) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Prevent scrolling when modal is open
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [isOpen, isProcessing, onClose]);
  
  if (!isOpen || !product) return null;
  
  // Handle checkbox changes
  const handleAgreementChange = (agreement) => {
    setAgreements(prev => ({
      ...prev,
      [agreement]: !prev[agreement]
    }));
  };
  
  // Check if user has agreed to all terms
  const hasAgreedToTerms = () => {
    return agreements.riskDisclosure && agreements.purchaseAgreement;
  };
  
  // Handle node selection and proceed to payment
  const handleNodeSelection = async () => {
    if (!selectedNodeId) {
      setError("Please select a node to continue");
      return;
    }
    
    if (!hasAgreedToTerms()) {
      setError("You must agree to the terms and conditions to proceed.");
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Show payment component
      setShowPaymentComponent(true);
      setConfirmationStep(2);
      setIsProcessing(false);
    } catch (error) {
      console.error('Error in node selection:', error);
      setError("Error processing your selection. Please try again.");
      setIsProcessing(false);
    }
  };
  
  // Handle payment success
  const handlePaymentSuccess = (paymentData) => {
    // Update purchase state with payment info
    setTransaction({
      productId: product.id,
      name: product.name,
      price: product.price,
      timestamp: new Date().toISOString(),
      transactionHash: paymentData.tx_hash || 'Pending confirmation',
      orderId: paymentData.order_id,
      nodeId: selectedNodeId,
      status: paymentData.status
    });
    
    // Move to success step
    setConfirmationStep(3);
    
    // Call the onPurchase callback
    if (onPurchase) {
      onPurchase({
        productId: product.id,
        orderId: paymentData.order_id,
        nodeId: selectedNodeId,
        status: paymentData.status
      });
    }
  };
  
  // Handle payment error
  const handlePaymentError = (errorMessage) => {
    setError(errorMessage);
    // Stay on the payment step
  };
  
  const formatWalletAddress = (address) => {
    if (!address) return '';
    return address.substring(0, 6) + '...' + address.substring(address.length - 4);
  };
  
  const renderStepOne = () => (
    <>
      <div className="purchase-modal-content">
        <div className="product-summary">
          <div className="product-image">
            <div className="device-container" style={{ transform: 'scale(0.5)' }}>
              <div className="device">
                <div className="device-signal">
                  <div className="signal-wave"></div>
                  <div className="signal-wave"></div>
                  <div className="signal-wave"></div>
                </div>
                <div className="device-screen"></div>
                <div className="device-logo">AEROFI</div>
                <div className="device-button"></div>
              </div>
            </div>
          </div>
          <div className="product-details">
            <h3>{product.name}</h3>
            <div className="product-price-confirm">
              <span className="price-currency">USDT</span>
              <span className="price-amount">{product.price}</span>
            </div>
            <div className="product-feature-list">
              <div className="product-feature">
                <span className="feature-label">Mining Factor:</span>
                <span className="feature-value">{product.miningFactor}x</span>
              </div>
              <div className="product-feature">
                <span className="feature-label">Expected Annual Yield:</span>
                <span className="feature-value">{product.baseAPY}</span>
              </div>
              <div className="product-feature">
                <span className="feature-label">Max Annual Yield:</span>
                <span className="feature-value">{product.maxAPY}</span>
              </div>
              <div className="product-feature">
                <span className="feature-label">Staking Capacity:</span>
                <span className="feature-value">{product.stakingCapacity}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Node Selector Component */}
        <NodeSelector
          nodes={nodes}
          onSelectNode={setSelectedNodeId}
          selectedNodeId={selectedNodeId}
          isLoading={isLoadingNodes}
          error={nodeError}
          productTier={product?.id}
          wallet={wallet}
        />
        
        <div className="payment-details">
          <h4>Payment Details</h4>
          
          <div className="payment-info">
            <div className="payment-row">
              <span className="payment-label">Price:</span>
              <span className="payment-value">{product.price} USDT</span>
            </div>
          </div>
          
          <div className="wallet-summary">
            <h4>Connected Wallet</h4>
            <div className="wallet-info-summary">
              <div className="wallet-type">
                {wallet.type === 'okx' && (
                  <div className="wallet-icon okx">
                    <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="32" rx="6" fill="#000"/>
                      <path d="M23 9H17V15H23V9Z" fill="#8C8C8C"/>
                      <path d="M15 9H9V15H15V9Z" fill="#8C8C8C"/>
                      <path d="M23 17H17V23H23V17Z" fill="#8C8C8C"/>
                      <path d="M15 17H9V23H15V17Z" fill="#8C8C8C"/>
                    </svg>
                  </div>
                )}
                {wallet.type === 'binance' && (
                  <div className="wallet-icon binance">
                    <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="32" rx="6" fill="#F0B90B"/>
                      <path d="M16 7L18.5 9.5L13.5 14.5L11 12L16 7Z" fill="white"/>
                      <path d="M19.5 10.5L22 13L13.5 21.5L11 19L19.5 10.5Z" fill="white"/>
                      <path d="M10 13L12.5 15.5L10 18L7.5 15.5L10 13Z" fill="white"/>
                      <path d="M22 19L24.5 21.5L22 24L19.5 21.5L22 19Z" fill="white"/>
                    </svg>
                  </div>
                )}
                {wallet.type === 'metamask' && (
                  <div className="wallet-icon metamask">
                    <svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect width="32" height="32" rx="6" fill="#FFFFFF"/>
                      <path d="M27.5933 3L17.6 11.1867L19.4133 6.4L27.5933 3Z" fill="#E17726"/>
                      <path d="M4.4 3L14.3067 11.28L12.5867 6.4L4.4 3Z" fill="#E27625"/>
                      <path d="M23.8933 21.6133L21.0667 25.9867L26.96 27.6267L28.6667 21.7067L23.8933 21.6133Z" fill="#E27625"/>
                      <path d="M3.34666 21.7067L5.04 27.6267L10.9333 25.9867L8.10666 21.6133L3.34666 21.7067Z" fill="#E27625"/>
                    </svg>
                  </div>
                )}
                <div className="wallet-address">
                  {formatWalletAddress(wallet.address)}
                </div>
              </div>
              <div className="wallet-balance">
                Balance: {wallet.balance} USDT
              </div>
            </div>
          </div>
        </div>
        
        {/* Add agreement checkboxes */}
        <div className="agreement-section">
          <h4>Terms and Agreements</h4>
          <div className="agreement-item">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={agreements.riskDisclosure}
                onChange={() => handleAgreementChange('riskDisclosure')}
              />
              <span className="checkbox-label">
                I have read and agree to the <a href="https://docs.aeronyx.network/articles/aledger-platform-general-risk-disclosure" target="_blank" rel="noopener noreferrer">ALEDGER PLATFORM GENERAL RISK DISCLOSURE</a>
              </span>
            </label>
          </div>
          
          <div className="agreement-item">
            <label className="checkbox-container">
              <input
                type="checkbox"
                checked={agreements.purchaseAgreement}
                onChange={() => handleAgreementChange('purchaseAgreement')}
              />
              <span className="checkbox-label">
                I have read and agree to the <a href="https://docs.aeronyx.network/articles/aledger-real-world-asset-package-purchase-agreement" target="_blank" rel="noopener noreferrer">ALEDGER REAL WORLD ASSET PACKAGE PURCHASE AGREEMENT</a>
              </span>
            </label>
          </div>
        </div>
      </div>
      
      <div className="purchase-modal-actions">
        <button 
          className="btn btn-outline" 
          onClick={onClose}
          disabled={isProcessing}
        >
          Cancel
        </button>
        <button 
          className="btn" 
          onClick={handleNodeSelection}
          disabled={isProcessing || !selectedNodeId || !hasAgreedToTerms()}
        >
          Continue with Selected Node
        </button>
      </div>
      
      {error && (
        <div className="purchase-error">
          <p>{error}</p>
        </div>
      )}
    </>
  );
  
  const renderStepTwo = () => (
    <div className="purchase-payment">
      <PaymentComponent 
        product={product}
        selectedNodeId={selectedNodeId}
        onPaymentSuccess={handlePaymentSuccess}
        onPaymentError={handlePaymentError}
        onClose={onClose}
      />
    </div>
  );
  
  const renderStepThree = () => (
    <div className="purchase-success">
      <div className="success-icon">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#10b981" strokeWidth="2"/>
          <path d="M8 12L11 15L16 9" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
      <h3>Order Placed Successfully!</h3>
      <p>Your purchase of the {product.name} is being processed.</p>
      <div className="success-details">
        <div className="success-item">
          <span className="success-label">Order ID:</span>
          <span className="success-value">{transaction?.orderId || ''}</span>
        </div>
        <div className="success-item">
          <span className="success-label">Status:</span>
          <span className="success-value">{transaction?.status || 'pending'}</span>
        </div>
      </div>
      <p className="success-message">You'll receive an email confirmation once your payment is processed. Payment confirmation may take up to 24 hours.</p>
      <div className="success-actions">
        <button 
          className="btn" 
          onClick={() => {
            onClose();
            window.location.href = '/portfolio';
          }}
        >
          View My Portfolio
        </button>
        <button 
          className="btn btn-outline" 
          onClick={onClose}
        >
          Continue Shopping
        </button>
      </div>
    </div>
  );
  
  return (
    <div className="purchase-modal-overlay">
      <div className="purchase-modal" ref={modalRef}>
        <button 
          className="purchase-modal-close" 
          onClick={onClose}
          disabled={isProcessing}
          aria-label="Close"
        >
          ×
        </button>
        
        <div className="purchase-modal-header">
          <h2>
            {confirmationStep === 1 && 'Confirm Your Purchase'}
            {confirmationStep === 2 && 'Complete Payment'}
            {confirmationStep === 3 && 'Order Confirmation'}
          </h2>
          
          <div className="purchase-steps">
            <div className={`step ${confirmationStep >= 1 ? 'active' : ''} ${confirmationStep > 1 ? 'completed' : ''}`}>1</div>
            <div className={`step-connector ${confirmationStep > 1 ? 'completed' : ''}`}></div>
            <div className={`step ${confirmationStep >= 2 ? 'active' : ''} ${confirmationStep > 2 ? 'completed' : ''}`}>2</div>
            <div className={`step-connector ${confirmationStep > 2 ? 'completed' : ''}`}></div>
            <div className={`step ${confirmationStep >= 3 ? 'active' : ''}`}>3</div>
          </div>
        </div>
        
        {confirmationStep === 1 && renderStepOne()}
        {confirmationStep === 2 && renderStepTwo()}
        {confirmationStep === 3 && renderStepThree()}
      </div>
    </div>
  );
};

export default PurchaseModal;
